import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClinicalTasksBySessionId } from '@/hooks/query/useQueryClinicalTasksBySessionId';
import {
  pendingWorkFirstTurnAtom,
  type PendingWorkFirstTurn,
  type UseCreateSessionWorkProject,
} from '@heidi/agents/data';

import type {
  SessionResearchVerb,
  SessionWorkLandingProps,
  SessionWorkSuggestion,
} from '@heidi/agents';
import { patientProfileDisplayName } from '@heidi/agents/contracts';
import {
  buildWorkHomeTaskRows,
  EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT,
  isProjectsComposerDraftEmpty,
  type ProjectsComposerPromptDraft,
} from '@heidi/agents/data';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import type { GetClinicalTasksResponse } from '@heidi/tasks/contracts';
import { useAtom } from 'jotai';

export interface SessionWorkLandingSuggestions {
  /** The session's linked patient, for the landing heading. `null` when unlinked. */
  patient: { name: string } | null;
  /** This session's open clinical tasks, ranked the way Work home ranks them. */
  suggestedWork: readonly SessionWorkSuggestion[];
}

export interface SessionWorkLandingResearchOptions {
  researchVerbs?: readonly SessionResearchVerb[];
  onOpenResearchVerb?: (verbId: string) => void;
}

export const useSessionWorkLandingResearch = ({
  researchVerbs = [],
  onOpenResearchVerb,
}: SessionWorkLandingResearchOptions = {}) => {
  const [composerPrefill, setComposerPrefill] = useState<string | undefined>();
  // The panel is locked to the session's patient, so a starter never seeds a
  // trigger suffix here: `@`/`#` open the mention menu to link a different
  // patient or session, which this panel must never offer.
  const onSelectResearchStarter = useCallback<
    SessionWorkLandingProps['onSelectResearchStarter']
  >(({ starter }) => {
    setComposerPrefill(starter);
  }, []);

  return {
    composerPrefill,
    landingProps: {
      onSelect: setComposerPrefill,
      researchVerbs,
      onSelectResearchStarter,
      onOpenResearchVerb,
    },
  };
};

/**
 * View-model for the in-session Work landing (`SessionWorkPanel`): the linked
 * patient's display name, plus this session's open clinical tasks (todo +
 * in_progress) ranked the way Work home ranks them
 * (`buildWorkHomeTaskRows`'s actionable-then-due-date sort).
 *
 * Not the richer `resolveWorkHomeWorkItems` pipeline (cold-start / EHR /
 * add-patient recommendations) — that lives in `packages/agents/src/ui/
 * projects/components/work-home-recommendations.ts` and isn't exported from
 * either `@heidi/agents` or `@heidi/agents/data`, so it stays out of scope
 * here rather than becoming a deep import.
 */
export const useSessionWorkLandingSuggestions = (
  sessionId: string,
  patientProfileId: string | null | undefined,
  sessionTasks: GetClinicalTasksResponse['tasks'] | undefined
): SessionWorkLandingSuggestions => {
  const { data: patientProfile } = useQueryPatientProfileById(
    patientProfileId ?? undefined,
    {
      enabled: Boolean(patientProfileId),
      // SessionWorkPanel only mounts inside the authenticated Scribe shell.
      isAuthenticated: true,
    }
  );
  const patientName = patientProfileDisplayName(patientProfile ?? null);
  const patient = patientName ? { name: patientName } : null;

  const openSessionTasks = useMemo(
    () =>
      (sessionTasks ?? []).filter(
        (task) =>
          task.status === ClinicalTaskStatus.todo ||
          task.status === ClinicalTaskStatus.in_progress
      ),
    [sessionTasks]
  );
  const suggestedWork = useMemo<readonly SessionWorkSuggestion[]>(
    () =>
      buildWorkHomeTaskRows(openSessionTasks, new Date()).map((row) => ({
        id: row.id,
        label: row.title,
        prompt: row.launch.suggestedPrompt ?? row.title,
      })),
    [openSessionTasks]
  );

  return { patient, suggestedWork };
};

/**
 * `useSessionWorkLandingSuggestions` with the session's clinical tasks fetched
 * here, gated to the landing: `showsLanding` is false while a project is
 * active, so mounting the panel on an existing project fetches nothing. The
 * patient profile stays on in every state — its name also seeds the panel's
 * "Working with" chip.
 */
export const useSessionWorkLandingViewModel = (
  sessionId: string,
  patientProfileId: string | null | undefined,
  showsLanding: boolean
): SessionWorkLandingSuggestions => {
  const { data } = useQueryClinicalTasksBySessionId(sessionId, { enabled: showsLanding });
  return useSessionWorkLandingSuggestions(sessionId, patientProfileId, data?.tasks);
};

interface PendingWorkFirstTurnHandoffOptions {
  sessionId: string;
  createSessionWorkProject: UseCreateSessionWorkProject['createSessionWorkProject'];
  /** The create mutation's in-flight flag. */
  isSubmitting: boolean;
}

export interface PendingWorkFirstTurnHandoff {
  /** The opening prompt, echoed as an optimistic user bubble until the create settles. */
  optimisticFirstTurnText: string | null;
  setOptimisticFirstTurnText: (next: string | null) => void;
  /** In-flight while the create round-trip runs, or a hand-off is stashed but
   *  the create hasn't fired yet — so the panel shows the optimistic transcript
   *  instead of flashing the empty state or lingering on an outgoing project. */
  isCreatingProject: boolean;
  /** Draft kept from a shortcut hand-off; absent on a normal open or send. */
  composerHostDraft?: {
    get: () => ProjectsComposerPromptDraft;
    set: (draft: ProjectsComposerPromptDraft) => void;
  };
}

const pendingTurnPreviewText = (turn: PendingWorkFirstTurn): string | null =>
  (turn.displayPrompt || turn.prompt).trim() || null;

/**
 * Consumes the floating bar's hand-off for `SessionWorkPanel` (see
 * `pendingWorkFirstTurnAtom`): the bar can't own the create because it
 * unmounts the moment the panel opens, so the panel — stable while open —
 * runs it here, where the mutation's `onSuccess` reliably lands.
 *
 * Also owns the optimistic first-turn bubble: seeded from the hand-off the
 * instant the panel opens (so sending reads like Evidence — the message shows
 * immediately — instead of flashing a bare loader through the create
 * round-trip), then handed off to `ProjectChat`'s own seeded-first-turn
 * preview (same bubble, same layout) once the project exists.
 *
 * Lives beside the landing view-model rather than in `SessionWorkPanel`
 * because that file sits at the line-count lint budget and
 * `src/features/projects/` is locked to new modules; the behaviour is
 * unchanged.
 */
export const usePendingWorkFirstTurnHandoff = ({
  sessionId,
  createSessionWorkProject,
  isSubmitting,
}: PendingWorkFirstTurnHandoffOptions): PendingWorkFirstTurnHandoff => {
  const [pendingTurn, setPendingTurn] = useAtom(pendingWorkFirstTurnAtom);
  const hasPendingForThisSession = pendingTurn?.sessionId === sessionId;
  // Captured before the consume effect clears the atom.
  const keptDraftRef = useRef<{
    sessionId: string;
    draft: ProjectsComposerPromptDraft;
  } | null>(null);
  if (keptDraftRef.current?.sessionId !== sessionId) {
    const handed = hasPendingForThisSession ? pendingTurn?.composerDraft : undefined;
    keptDraftRef.current =
      handed && !isProjectsComposerDraftEmpty(handed)
        ? { sessionId, draft: handed }
        : null;
  }
  const composerHostDraft = useMemo(
    () => ({
      get: () => keptDraftRef.current?.draft ?? EMPTY_PROJECTS_COMPOSER_PROMPT_DRAFT,
      set: (draft: ProjectsComposerPromptDraft) => {
        if (keptDraftRef.current)
          keptDraftRef.current = { ...keptDraftRef.current, draft };
      },
    }),
    []
  );

  const [optimisticFirstTurnText, setOptimisticFirstTurnText] = useState<string | null>(
    () =>
      pendingTurn?.sessionId === sessionId ? pendingTurnPreviewText(pendingTurn) : null
  );

  // Defense in depth with host `key={sessionId}`: clear/reseed optimistic text
  // when the session identity changes so a retained prompt cannot flash across
  // sessions (workPanel invariant).
  useEffect(() => {
    setOptimisticFirstTurnText(
      pendingTurn?.sessionId === sessionId ? pendingTurnPreviewText(pendingTurn) : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionId only; pending hand-offs owned by consume effect
  }, [sessionId]);

  // No stand-in marker is needed for the create->chat swap: the optimistic
  // bubble keeps one stable item id across the provisional -> bound flip on
  // the same `ProjectChat` instance, so its mount-time entrance can't replay
  // (see `useOptimisticFirstTurn`); `seedOptimisticFirstTurn` in the create's
  // `onCreated` covers remount restores.
  useEffect(() => {
    if (!hasPendingForThisSession || !pendingTurn) return;
    // Consume once. `createSessionWorkProject` also has its own in-flight
    // guard as a backstop against a double-fire.
    setPendingTurn(null);
    // Always create — even if this session already has a project. A bar-send /
    // chip starts a fresh project (Evidence's "always new" rule); create's
    // `onCreated` points the panel at the new one, so it swaps in and the old
    // project stays reachable via the in-session project selector.
    // Seed the optimistic bubble here too (covers late hand-offs the lazy
    // initialiser above missed).
    setOptimisticFirstTurnText(pendingTurnPreviewText(pendingTurn));
    createSessionWorkProject({
      prompt: pendingTurn.prompt,
      linked: pendingTurn.linked,
      displayPrompt: pendingTurn.displayPrompt,
      files: pendingTurn.files,
      attachment_ids: pendingTurn.attachment_ids,
      optimisticAttachments: pendingTurn.optimisticAttachments,
      runFlags: pendingTurn.runFlags,
      computerUseSkill: pendingTurn.computerUseSkill,
      approvalMode: pendingTurn.approvalMode,
      analytics: {
        surface: 'in_session',
        ...pendingTurn.analytics,
        clinical_session_id: sessionId,
      },
    });
  }, [
    createSessionWorkProject,
    hasPendingForThisSession,
    pendingTurn,
    sessionId,
    setPendingTurn,
  ]);

  const isCreatingProject = isSubmitting || hasPendingForThisSession;

  // Hand the optimistic bubble off once the create settles. On a successful
  // bind the project's store entry (recorded in `onCreated` via
  // `seedOptimisticFirstTurn`, `animated: true`) owns the bubble statically and
  // survives a `ProjectChat` remount; on failure there's no project and the
  // bubble is dropped. Clearing at settle — not at `items.length > 0` — is what
  // stops the prop shadowing that store entry: a sticky prop (`animated: false`)
  // would replay the entrance on a remount during the bound-but-pre-first-message
  // window, and `clearPreview` can't drop a prop-owned bubble on a post-bind
  // reject. Held while creating so a re-create's new bubble isn't cleared early.
  useEffect(() => {
    if (!isCreatingProject && optimisticFirstTurnText) {
      setOptimisticFirstTurnText(null);
    }
  }, [isCreatingProject, optimisticFirstTurnText]);

  return {
    optimisticFirstTurnText,
    setOptimisticFirstTurnText,
    isCreatingProject,
    composerHostDraft: keptDraftRef.current ? composerHostDraft : undefined,
  };
};
